/**
 * French language keywords for category prediction
 */
import type {
  AccommodationSubcategoryKeywords,
  ContextKeywords,
  DrinksConfig,
  DrinksSubcategoryKeywords,
  EntertainmentSubcategoryKeywords,
  FoodConfig,
  FoodSubcategoryKeywords,
  HealthcareSubcategoryKeywords,
  LanguageKeywords,
  MLPatternKeywords,
  ShoppingContextKeywords,
  ShoppingSubcategoryKeywords,
  StemmingConfig,
  TransportConfig,
  TransportKeywords,
  TransportSubcategoryKeywords,
  UtilitiesSubcategoryKeywords,
} from '../types';

// Transport-related keywords for disambiguation
export const transportKeywords: TransportKeywords = {
  general: [
    'train',
    'autobus',
    'vol',
    'avion',
    'métro',
    'metro',
    'tramway',
    'taxi',
    'transport',
    'voyage',
    'compagnie aérienne',
    'chemin de fer',
    'car',
    'navette',
    'ferry',
    'bateau',
    'voiture',
    'trajet',
    'aéroport',
    'gare',
    'station',
  ],
  tickets: ['billet', 'billets', 'ticket', 'tarif', 'abonnement'],
  tripContext: ['voyage', 'trajet', 'vacances', 'conduite', 'conduire'],
};

// Transport subcategory keywords
export const transportSubcategories: TransportSubcategoryKeywords = {
  car: [
    'paiement voiture',
    'crédit auto',
    'crédit voiture',
    'lavage voiture',
    'lavage auto',
    'assurance voiture',
    'assurance auto',
    'détailage',
    'contrôle technique',
  ],
  // FR common words only — brand names live in `../shared/brands.ts`.
  bus: ['bus', 'autobus', 'autocar', 'navette'],
  train: ['train', 'chemin de fer', 'billet de train', 'train de banlieue'],
  tram: ['tramway', 'tram'],
  flight: [
    'vol',
    'avion',
    'aérien',
    'compagnie aérienne',
    'billet avion',
    'carte embarquement',
  ],
  bike: ['vélo', 'bicyclette', 'cyclisme', 'vtt', 'trottinette'],
  boat: ['bateau', 'navire', 'ferry', 'croisière', 'yacht', 'voilier'],
  parking: [
    'parking',
    'stationnement',
    'parcmètre',
    'place de parking',
    'parking souterrain',
  ],
  taxi: ['taxi', 'vtc', 'course taxi'],
  rideshare: ['covoiturage'],
  metro: [
    'métro',
    'rer',
    'métropolitain',
    'métro montréal',
    'métro paris',
    'métro lyon',
    'métro marseille',
  ],
  fuel: [
    'essence',
    'carburant',
    'gazole',
    'diesel',
    'sans plomb',
    'station service',
    'station essence',
    'intermarché station',
  ],
  toll: ['péage', 'barrière de péage', 'autoroute à péage'],
  rental: ['location voiture', 'location auto', 'rent a car'],
  maintenance: [
    'entretien voiture',
    'réparation auto',
    'vidange',
    'pneu',
    'pneus',
    'contrôle technique',
    'mécanicien',
    'garagiste',
    'freins',
    'transmission',
  ],
  airport: [
    'aéroport',
    'terminal aéroport',
    'parking aéroport',
    'navette aéroport',
    'transfert aéroport',
    'salon aéroport',
  ],
};

// Only the generic word is shared by food and healthcare: on its own it
// cannot say sports nutrition or health, so the cloud tier breaks that
// tie. Specific products go to one parent so the free tier can answer.
const supplementsKeywords: string[] = [
  'compléments alimentaires',
  'complément alimentaire',
  'complements alimentaires',
  'complement alimentaire',
];
const sportsNutritionKeywords: string[] = [
  'protéine',
  'proteine',
  'whey',
  'créatine',
  'creatine',
];
const healthSupplementsKeywords: string[] = [
  'collagène',
  'collagene',
  'vitamines',
  'vitamine',
  'omega 3',
  'oméga 3',
  'omega-3',
  'probiotiques',
  'magnésium',
  'magnesium',
  'multivitamines',
];

// Food subcategory keywords (partial list for demonstration)
export const foodSubcategories: Partial<FoodSubcategoryKeywords> = {
  // FR common words only — brands + universal food terms live in
  // `../shared/brands.ts`.
  pizza: [],
  sushi: [],
  hamburger: [],
  sandwich: ['croque-monsieur'],
  soup: ['soupe', 'potage', 'velouté'],
  steak: ['entrecôte', 'faux-filet', 'rumsteck', 'côte de bœuf', 'bavette'],
  salad: [
    'salade',
    'salade césar',
    'salade verte',
    'salade composée',
    'salade niçoise',
  ],
  pastry: ['pâtisserie', 'opéra'],
  dessert: ['gâteau', 'tarte', 'biscuits', 'tarte tatin', 'éclair'],
  candy: ['bonbons', 'friandises', 'sucreries', 'chocolat', 'sucette'],
  icecream: ['glace', 'crème glacée', 'glacier'],
  snacks: ['en-cas', 'biscuits salés', 'cacahuètes', 'fruits secs'],
  fruit: ['pomme', 'banane', 'baies'],
  vegetables: ['légume', 'légumes', 'verdure'],
  meat: [
    'boucherie',
    'viande',
    'bœuf',
    'porc',
    'agneau',
    'poulet',
    'dinde',
    'jambon',
    'lardons',
    'saucisson',
  ],
  seafood: [
    'fruits de mer',
    'poisson',
    'saumon',
    'thon',
    'crevettes',
    'homard',
    'crabe',
    'huîtres',
    'moules',
  ],
  dairy: [
    'produits laitiers',
    'lait',
    'fromage',
    'yaourt',
    'beurre',
    'crème',
    'œufs',
    'fromage blanc',
  ],
  breakfast: ['petit-déjeuner', 'petit déjeuner', 'confiture', 'œufs au plat'],
  restaurant: [
    'restaurant',
    'resto',
    'bistrot',
    'italien',
    'chinois',
    'mexicain',
    'indien',
    'thaï',
    'vietnamien',
    'coréen',
    'japonais',
    'libanais',
    'brasserie',
    'restauration rapide',
    'déjeuner',
    'midi',
    'repas de midi',
    'dîner',
    'souper',
    'repas du soir',
  ],
  bakery: ['boulangerie', 'pain', 'baguette', 'viennoiserie'],
  // Venues only: in French a bare 'café' is the drink (drinks/coffee).
  cafe: [
    'cafétéria',
    'café-restaurant',
    'café restaurant',
    'salon de thé',
    'coffee shop',
    'brûlerie',
    'paul',
  ],
  delivery: ['livraison', 'à emporter'],
  groceries: ['courses', 'supermarché', 'épicerie', 'marché', 'metro epicerie'],
  supplements: [...sportsNutritionKeywords, ...supplementsKeywords],
};

export const drinksSubcategories: DrinksSubcategoryKeywords = {
  // FR common words only — brands live in `../shared/brands.ts`.
  // Bare "water" words stay out: they collide with utilities/water.
  drinks: ['boisson', 'boissons', 'rafraîchissement'],
  water: [
    'eau minérale',
    'eau gazeuse',
    'eau pétillante',
    'eau plate',
    "bouteille d'eau",
    "pack d'eau",
  ],
  juice: ['jus', "jus d'orange", 'jus de fruits', 'jus de pomme'],
  soda: ['soda', 'sodas', 'limonade', 'boisson gazeuse', 'diabolo', 'orangina'],
  coffee: [
    'café',
    'cafe',
    'cafés',
    'un café',
    'petit noir',
    'déca',
    'café au lait',
    'café crème',
    'grand crème',
    'café allongé',
    'café glacé',
    'expresso',
  ],
  tea: ['thé', 'thé vert', 'thé glacé', 'tisane', 'infusion'],
  energy: [
    'boisson énergisante',
    'boissons énergisantes',
    'boisson énergétique',
    'boisson isotonique',
  ],
  // Bare 'pression' and 'demi' misfire ('impression', 'demi-pension').
  beer: [
    'bière',
    'bières',
    'binouze',
    'pinte',
    'bière pression',
    'une pression',
    'un demi',
    'panaché',
  ],
  wine: [
    'vin',
    'vins',
    'vin rouge',
    'vin blanc',
    'vin rosé',
    'verre de vin',
    'bouteille de vin',
    'caviste',
  ],
  cocktails: ['kir', 'kir royal'],
  spirits: [
    'spiritueux',
    'rhum',
    'calvados',
    'armagnac',
    'eau-de-vie',
    'pastis',
    'digestif',
  ],
  // Any 'verres' is also eyeglass lenses ('des verres de contact'), and
  // 'un verre' would tie 'un verre de vin' with wine.
  alcohol: [
    'alcool',
    'alcools',
    'apéro',
    'apéritif',
    'prendre un verre',
    'boire un verre',
  ],
  bar: ['bar', 'pub', 'taverne', 'bar à vin', 'buvette'],
};

// Context keywords
export const contextKeywords: ContextKeywords = {
  flight: ['bagage', 'vol'],
  fuel: [
    'voyage',
    'trajet',
    'vacances',
    'conduire',
    'voiture',
    'véhicule',
    'kilomètres',
  ],
  transportTickets: [
    'billet',
    'billets',
    'ticket',
    'pass',
    'tarif',
    'transport',
    'trajet',
    'voyage',
    'parcours',
  ],
};

// Entertainment subcategory keywords
const entertainmentSubcategories: EntertainmentSubcategoryKeywords = {
  // FR common words only — brands + universal terms live in
  // `../shared/brands.ts`.
  movies: [
    'film',
    'cinéma',
    'séance',
    'projection',
    'salle de cinéma',
    'matinée',
  ],
  events: [
    'concert',
    'spectacle',
    'musique live',
    'performance',
    'musée',
    'galerie',
    "galerie d'art",
    'exposition',
    'centre culturel',
    'événement',
    'conférence',
    'convention',
    'rencontre',
    'festival',
    'foire',
    'carnaval',
    'célébration',
    'fête',
    'théâtre',
    'pièce',
    'comédie musicale',
    'opéra',
    'vie nocturne',
    'club',
    'boîte de nuit',
    'billet',
  ],
  gaming: ['jeu', 'jeu vidéo', 'jeux'],
  books: [
    'livre',
    'lecture',
    'roman',
    'littérature',
    'librairie',
    'bibliothèque',
    'magazine',
    'journal',
  ],
  outdoor: [
    'parc',
    "parc d'attractions",
    'parc à thème',
    'randonnée',
    'plein air',
    'montagne',
    'escalade',
    'alpinisme',
    'nature',
    'aventure',
    'pêche',
    'chasse',
  ],
  music: [
    'musique',
    'concert',
    'spectacle',
    'chanson',
    'artiste',
    'groupe',
    'vinyle',
  ],
  streaming: ['abonnement', 'série'],
  fitness: ['musculation', 'entrainement', 'entraînement'],
  sports: ['sport', 'match', 'équipe', 'stade', 'football', 'basket'],
  photography: [
    'photo',
    'photographie',
    'appareil photo',
    'objectif',
    'photographe',
    'séance photo',
    'shooting photo',
  ],
  podcasts: ['talk-show'],
  water: [
    'plongée',
    'natation',
    'sports nautiques',
    'piscine',
    'voile',
    'planche à voile',
    'ski nautique',
    'plage',
    'aquatique',
    'marin',
  ],
};

// Shopping subcategory keywords
const shoppingSubcategories: ShoppingSubcategoryKeywords = {
  // FR common words only — brands live in `../shared/brands.ts`.
  clothes: [
    'vêtements',
    'habillement',
    'mode',
    'tenue',
    'habits',
    'prêt-à-porter',
    'chemise',
    'pantalon',
    'robe',
    'veste',
    'manteau',
  ],
  electronics: [
    'électronique',
    'informatique',
    'ordinateur',
    'téléphone',
    'portable',
    'tablette',
    'écouteurs',
    'casque',
    'électroménager',
    'réfrigérateur',
    'lave-linge',
    'sèche-linge',
    'lave-vaisselle',
    'micro-ondes',
    'four',
  ],
  groceries: [
    'courses',
    'alimentation',
    'supermarché',
    'épicerie',
    'metro epicerie',
  ],
  online: ['en ligne', 'achat en ligne', 'e-commerce', 'colis'],
  furniture: [
    'meubles',
    'mobilier',
    'décoration',
    'chaise',
    'table',
    'canapé',
    'lit',
    'bureau',
    'décoration maison',
    'intérieur',
    'amélioration maison',
  ],
  gifts: [
    'cadeau',
    'présent',
    'offrir',
    'anniversaire',
    'noël',
    'fleurs',
    'carte',
  ],
  services: [
    'services',
    'prestation',
    'plombier',
    'électricien',
    'réparation',
    'entretien',
    'technicien',
    'peintre',
    'menuisier',
    'serrurier',
    'nettoyage',
    'installation',
  ],
  shoes: [
    'chaussures',
    'baskets',
    'bottes',
    'sandales',
    'espadrilles',
    'bottines',
  ],
  books: ['livre', 'librairie', 'roman', 'bd', 'magazine', 'journal'],
  toys: ['jouets', 'jouet', 'peluche', 'poupée'],
  mall: ['centre commercial', 'galerie marchande'],
  market: [
    'marché',
    'marché local',
    'marché producteur',
    'brocante',
    'marché aux puces',
  ],
  department: ['grand magasin', 'bazar'],
  jewelry: [
    'bijoux',
    'bijouterie',
    'or',
    'argent',
    'bague',
    'collier',
    'bracelet',
    'montre',
    'accessoires',
    'sacs',
    'portefeuille',
    'ceinture',
    'écharpe',
    'chapeau',
  ],
  cosmetics: [
    'cosmétique',
    'maquillage',
    'beauté',
    'parfum',
    'cosmétiques',
    'soin de la peau',
    'soin des cheveux',
    'salon',
    'spa',
  ],
  sports: ['sport', 'équipement sportif'],
  hardware: ['bricolage', 'outillage', 'quincaillerie'],
  garden: [
    'jardin',
    'jardinage',
    'plantes',
    'fleurs',
    'jardinerie',
    'pépinière',
    'terrasse',
  ],
};

// Accommodation subcategory keywords
const accommodationSubcategories: AccommodationSubcategoryKeywords = {
  // FR common words only — brands live in `../shared/brands.ts`.
  hotel: [
    'hôtel',
    'auberge',
    'chambre hôtel',
    'suite',
    'resort',
    'hébergement',
    'station balnéaire',
    'station de ski',
    'spa resort',
    'tout inclus',
    'motel',
    'relais routier',
    'hébergement routier',
    'chambre',
    'salle',
    'logement',
  ],
  airbnb: [
    'location vacances',
    'gîte',
    'location saisonnière',
    'location courte durée',
  ],
  apartment: [
    'appartement',
    'studio',
    'logement',
    'loft',
    'duplex',
    'penthouse',
    'résidence',
  ],
  house: [
    'maison',
    'villa',
    'chalet',
    'pavillon',
    'cottage',
    'résidence',
    'bungalow',
  ],
  rent: [
    'loyer',
    'location',
    'bail',
    'propriétaire',
    'locataire',
    'loyer mensuel',
  ],
  hostel: [
    'auberge de jeunesse',
    'hostel',
    'dortoir',
    'hébergement économique',
    'backpacker',
  ],
  camping: [
    'camping',
    'tente',
    'caravane',
    'camping-car',
    'emplacement',
    'glamping',
  ],
};

// Healthcare subcategory keywords
const healthcareSubcategories: HealthcareSubcategoryKeywords = {
  // FR common words only — brands live in `../shared/brands.ts`.
  doctor: [
    'médecin',
    'docteur',
    'clinique',
    'médecin généraliste',
    'contrôle médical',
    'visite médicale',
  ],
  pharmacy: [
    'pharmacie',
    'médicament',
    'ordonnance',
    'prescription médicale',
    'remède',
    'parapharmacie',
    'pharmacie de garde',
  ],
  hospital: [
    'hôpital',
    'urgence',
    'urgences',
    'chirurgie',
    'opération',
    "salle d'urgence",
    'hospitalisation',
    'ambulance',
    'laboratoire',
    'analyse de sang',
    'test médical',
    'diagnostic',
  ],
  dental: [
    'dentiste',
    'dentaire',
    'dents',
    'orthodontiste',
    'orthodontie',
    'nettoyage dentaire',
    'appareil dentaire',
    'rendez-vous dentiste',
  ],
  vision: [
    'opticien',
    'optométriste',
    'ophtalmologue',
    'lunettes',
    'lentilles',
    'lentilles de contact',
    'examen de vue',
    'yeux',
    'vue',
  ],
  specialist: [
    'spécialiste',
    'dermatologue',
    'cardiologue',
    'pédiatre',
    'psychiatre',
    'thérapie',
    'consultation spécialiste',
    'santé mentale',
    'psychologue',
    'conseil',
    'séance de thérapie',
    'kinésithérapie',
    'rééducation',
    'ergothérapie',
  ],
  insurance: [
    'assurance',
    'assurance maladie',
    'assurance santé',
    'mutuelle',
    'couverture',
    'franchise',
    'remboursement',
  ],
  fitness: [
    'salle de sport',
    'abonnement gym',
    'centre de fitness',
    'entraînement',
    'entraîneur personnel',
    'exercice',
    'musculation',
    'bien-être',
    'centre de bien-être',
    'médecine alternative',
    'acupuncture',
    'massage',
  ],
  supplements: [...healthSupplementsKeywords, ...supplementsKeywords],
};

// Utilities subcategory keywords
const utilitiesSubcategories: UtilitiesSubcategoryKeywords = {
  // FR common words only — brand providers live in `../shared/brands.ts`.
  electricity: ['électricité', 'courant', 'énergie', 'facture électricité'],
  water: ['eau', 'service des eaux', 'facture eau', 'égout', 'assainissement'],
  gas: [
    'gaz',
    'gaz naturel',
    'chauffage',
    'facture gaz',
    'propane',
    'chaleur',
    'chaudière',
    'radiateur',
    'système de chauffage',
  ],
  internet: ['internet', 'fibre', 'box', 'connexion'],
  phone: [
    'téléphone',
    'mobile',
    'portable',
    'forfait',
    'forfait mobile',
    'facture téléphone',
  ],
  trash: [
    'ordures',
    'poubelles',
    'déchets',
    'recyclage',
    'collecte',
    'poubelle',
    'collecte des déchets',
  ],
  maintenance: ['entretien', 'réparation', 'charges', 'syndic', 'copropriété'],
  streaming: [
    'abonnement tv',
    'abonnement streaming',
    'service streaming',
    'télévision',
    'câble',
    'satellite',
  ],
};

// Shopping context keywords for better subcategory detection
const shoppingContext: ShoppingContextKeywords = {
  clothes: ['mode', 'boutique'],
  electronics: ['technologie', 'appareil'],
  furniture: ['décoration', 'maison'],
  online: ['commande', 'suivi'],
  groceries: ['hebdomadaire', 'mensuel', 'liste de courses'],
  services: ['réparer', 'dépannage', 'cassé', 'installer', 'entretien'],
};

// ML model pattern keywords for fast matching (French translations of common terms)
const mlPatterns: MLPatternKeywords = {
  expense: {
    transport: [
      'uber',
      'taxi',
      'bus',
      'train',
      'vol',
      'essence',
      'stationnement',
      'métro',
      'vacances',
      'voyage',
      'trajet',
      'transport',
      'autobus',
      'avion',
      // Canadian brands and services
      'via rail',
      'go train',
      'go transit',
      'ttc',
      'stm',
      'sto',
      'oct transpo',
      'translink',
      'calgary transit',
      'edmonton transit',
      'air canada',
      'westjet',
      'porter airlines',
      'petro-canada',
      'esso',
      'shell',
      'canadian tire gas',
      'costco essence',
    ],
    food: [
      'restaurant',
      'déjeuner',
      'dîner',
      'épicerie',
      'café',
      'pizza',
      'nourriture',
      'petit-déjeuner',
      'repas',
      'courses',
      'marché',
      'boulangerie',
      'supermarché',
      // Canadian food chains and groceries
      'tim hortons',
      'tims',
      'second cup',
      'starbucks',
      'a&w',
      "harvey's",
      'swiss chalet',
      'boston pizza',
      "kelsey's",
      'the keg',
      'loblaws',
      'superstore',
      'metro',
      'sobeys',
      'iga',
      'provigo',
      'maxi',
      'costco',
      'walmart',
      'canadian tire',
      'dollarama',
      'jean coutu',
      'pharmaprix',
      'rona',
      'home depot',
      'lcbo',
      'saq',
    ],
    entertainment: [
      'netflix',
      'spotify',
      'film',
      'cinéma',
      'concert',
      'jeu',
      'gym',
      'sport',
      'spectacle',
      'théâtre',
      'musée',
      // Canadian entertainment and media
      'cbc',
      'radio-canada',
      'tva',
      'crave',
      'bell média',
      'rogers',
      'telus',
      'cineplex',
      'landmark cinemas',
      'goodlife fitness',
      'la fitness',
      'ymca',
      'centre bell',
      'rogers place',
      'scotiabank arena',
      'cn tower',
      'musée des beaux-arts',
      'musée mccord',
      'musée de la civilisation',
    ],
    shopping: [
      'amazon',
      'carrefour',
      'auchan',
      'vêtements',
      'achats',
      'magasin',
      'boutique',
      'centre commercial',
      'courses',
      // Canadian shopping and retail
      'canadian tire',
      'walmart',
      'costco',
      'loblaws',
      'superstore',
      'sobeys',
      'metro',
      'iga',
      'provigo',
      'maxi',
      'dollarama',
      'winners',
      'marshalls',
      "hudson's bay",
      'la baie',
      'simons',
      'reitmans',
      'sportchek',
      'marks',
      'princess auto',
      'crappy tire',
      'best buy',
      'future shop',
      'staples',
      'bureau en gros',
    ],
    healthcare: [
      'médecin',
      'docteur',
      'hôpital',
      'pharmacie',
      'dentiste',
      'médical',
      'clinique',
      'santé',
      'consultation',
      // Canadian healthcare and pharmacies
      'jean coutu',
      'pharmaprix',
      'shoppers drug mart',
      'rexall',
      'lawtons',
      "remedy's rx",
      'uniprix',
      'brunet',
      'familiprix',
      'proxim',
      'hospital general',
      'centre hospitalier',
      'clsc',
      'walk-in clinic',
      'clinique sans rendez-vous',
      'urgence',
      'emergency',
    ],
    utilities: [
      'électricité',
      'eau',
      'internet',
      'téléphone',
      'facture',
      'gaz',
      'chauffage',
      'charges',
      'abonnement',
      // Canadian utility companies
      'hydro-québec',
      'hydro one',
      'bc hydro',
      'saskpower',
      'manitoba hydro',
      'nb power',
      'nova scotia power',
      'nl hydro',
      'bell',
      'rogers',
      'telus',
      'shaw',
      'videotron',
      'cogeco',
      'eastlink',
      'freedom mobile',
      'fido',
      'koodo',
      'virgin mobile',
      'chatr',
      'public mobile',
      'lucky mobile',
      'enbridge',
      'fortisbc',
      'union gas',
    ],
    accommodation: [
      'hôtel',
      'airbnb',
      'loyer',
      'appartement',
      'location',
      'logement',
      'hébergement',
      'chambre',
      // Canadian hotel chains and accommodations
      'fairmont',
      'delta hotels',
      'marriott',
      'hilton',
      'best western',
      'holiday inn',
      'comfort inn',
      'ramada',
      'sandman',
      'accent inns',
      'château frontenac',
      'banff springs',
      'whistler village',
      'blue mountain resort',
      'mont tremblant',
      'airbnb',
      'vrbo',
      'booking.com',
      'expedia.ca',
    ],
    education: [
      'cours',
      'classe',
      'école',
      'université',
      'collège',
      'livre',
      'manuel',
      'formation',
      'certification',
      'atelier',
      'séminaire',
      'frais de scolarité',
      'apprentissage',
      'étude',
      'lycée',
      'enseignement',
      // Canadian educational institutions and services
      'université de montréal',
      'mcgill',
      'université laval',
      'uqam',
      'concordia',
      "université d'ottawa",
      'carleton',
      'queens',
      'university of toronto',
      'york university',
      'ryerson',
      'waterloo',
      'western',
      'ubc',
      'sfu',
      'university of calgary',
      'university of alberta',
      'cégep',
      'college',
      'seneca',
      'centennial',
      'sheridan',
      'algonquin',
      'chapters indigo',
      'renaud-bray',
      'archambault',
      'staples',
      'bureau en gros',
    ],
  },
  group: {
    friends: [
      'amis',
      'amitié',
      'copains',
      'potes',
      'bande',
      'groupe',
      'soirée',
      'sortie',
      'cercle',
      'équipe',
      'compagnons',
      'dîner',
      'jeu',
      'weekend',
      'soirée jeux',
    ],
    family: [
      'famille',
      'parents',
      'frères',
      'soeurs',
      'fratrie',
      'proches',
      'réunion',
      'grands-parents',
      'enfants',
      'cousins',
      'parenté',
      'foyer',
    ],
    couple: [
      'couple',
      'partenaire',
      'conjoint',
      'mari',
      'femme',
      'époux',
      'épouse',
      'amoureux',
      'petit ami',
      'petite amie',
      'mariage',
      'union',
    ],
    events: [
      'anniversaire',
      'fête',
      'célébration',
      'événement',
      'mariage',
      'baptême',
      'communion',
      'réception',
      'soirée',
      'gala',
      'cérémonie',
    ],
    roommates: [
      'colocataires',
      'colocation',
      'coloc',
      'colocataire',
      'appartement partagé',
      'chambre',
      'loyer',
      'charges',
    ],
    trip: [
      'voyage',
      'vacances',
      'séjour',
      'excursion',
      'tourisme',
      'week-end',
      'escapade',
      'road trip',
      'visite',
      'découverte',
      // Canadian destinations and travel terms
      'banff',
      'jasper',
      'whistler',
      'mont tremblant',
      'quebec city',
      'montreal',
      'toronto',
      'vancouver',
      'halifax',
      'calgary',
      'ottawa',
      'niagara falls',
      'muskoka',
      'cottage',
      'chalet',
      'camping',
      'parc national',
      'parcs canada',
      'via rail',
      'rocky mountains',
      'maritimes',
      'gaspésie',
      'charlevoix',
      'tadoussac',
    ],
    work: [
      'travail',
      'bureau',
      'collègues',
      'équipe',
      'entreprise',
      'société',
      'professionnel',
      'déjeuner travail',
      'réunion',
      'conférence',
    ],
  },
};

// Food configuration
const foodConfig: FoodConfig = {
  obviousSubcategories: ['fruits', 'légumes'],
  subcategoryPriority: ['pizza', 'hamburger', 'sandwich', 'soupe', 'cafe'],
};

const drinksConfig: DrinksConfig = {
  subcategoryPriority: [
    'cocktails',
    'beer',
    'wine',
    'spirits',
    'energy',
    'coffee',
    'tea',
    'juice',
    'soda',
    'water',
    'bar',
    'alcohol',
    'drinks',
  ],
};

// Transport configuration
const transportConfig: TransportConfig = {
  subcategoryPriority: [
    'location',
    'vol',
    'aeroport',
    'taxi',
    'covoiturage',
    'voiture',
    'velo',
    'bateau',
    'train',
    'tramway',
  ],
};

// French stemming configuration
const stemming: StemmingConfig = {
  suffixes: [
    'ant',
    'ent',
    'tion',
    'ment',
    'age',
    'isme',
    'able',
    'ible',
    'eur',
    'euse',
    'té',
    'er',
    'ir',
    'oir',
    's',
  ],
};

// Complete French language keyword configuration
export const frKeywords: LanguageKeywords = {
  transport: transportKeywords,
  transportSubcategories,
  foodSubcategories: foodSubcategories as FoodSubcategoryKeywords,
  drinksSubcategories,
  entertainmentSubcategories,
  shoppingSubcategories,
  accommodationSubcategories,
  healthcareSubcategories,
  utilitiesSubcategories,
  context: contextKeywords,
  shoppingContext,
  foodConfig,
  drinksConfig,
  transportConfig,
  mlPatterns,
  stemming,
};
